import { ensureValidHandle } from "@atproto/syntax";
import {
  type Author,
  type Post,
  quoteUri,
  type RecordEvent,
  type ThreadEvent,
} from "./model.ts";
import {
  parsePost,
  validateBacklinks,
  validateDocument,
  validateProfile,
  validateRecord,
} from "./schema.ts";
import { type Network, publicUrl } from "./network.ts";
import { blobUrl, didDocumentUrl, postUri } from "./uri.ts";

interface Identity {
  did: string;
  pds: string;
  aliases: string[];
}

export class Atproto {
  #identities = new Map<string, Promise<Identity>>();
  #handles = new Map<string, Promise<string>>();
  #posts = new Map<string, Promise<Post>>();

  constructor(private network: Network, private signal: AbortSignal) {}

  resolveHandle(handle: string): Promise<string> {
    handle = handle.toLowerCase();
    ensureValidHandle(handle);
    let pending = this.#handles.get(handle);
    if (!pending) {
      pending = (async () => {
        const records = await this.network.txt(
          `_atproto.${handle}`,
          this.signal,
        ).catch(() => []);
        this.signal.throwIfAborted();
        const dids = [
          ...new Set(
            records.map((parts) => parts.join("")).filter((s) =>
              s.startsWith("did=")
            ).map((s) => s.slice(4)),
          ),
        ];
        if (dids.length > 1) {
          throw new Error("Handle has conflicting DNS records.");
        }
        const did = dids[0] ??
          (await this.network.text(
            new URL(`https://${handle}/.well-known/atproto-did`),
            this.signal,
          )).trim();
        didDocumentUrl(did);
        return did;
      })();
      this.#handles.set(handle, pending);
    }
    return pending;
  }

  identity(did: string): Promise<Identity> {
    let pending = this.#identities.get(did);
    if (!pending) {
      pending = (async () => {
        const { value: doc, errors } = validateDocument(
          await this.network.json(didDocumentUrl(did), this.signal).catch(
            (error) => {
              this.signal.throwIfAborted();
              if (!did.startsWith("did:plc:")) throw error;
              return this.network.json(
                new URL(`https://plc.directory/${did}`),
                this.signal,
              );
            },
          ),
        );
        if (errors) throw new Error("Invalid DID document.");
        if (doc.id !== did) {
          throw new Error("DID document identity does not match.");
        }
        const service = doc.service?.find((s) =>
          (s.id === "#atproto_pds" || s.id === `${did}#atproto_pds`) &&
          s.type === "AtprotoPersonalDataServer"
        );
        if (typeof service?.serviceEndpoint !== "string") {
          throw new Error("Identity has no PDS endpoint.");
        }
        const pds = new URL(service.serviceEndpoint);
        publicUrl(pds);
        if (pds.search) {
          throw new Error("PDS endpoint must not contain a query.");
        }
        return {
          did,
          pds: pds.href.replace(/\/$/, ""),
          aliases: doc.alsoKnownAs ?? [],
        };
      })();
      this.#identities.set(did, pending);
    }
    return pending;
  }

  async canonical(input: string): Promise<string> {
    const uri = postUri(input);
    if (!uri.host.startsWith("did:")) {
      const handle = uri.host.toLowerCase();
      const did = await this.resolveHandle(handle);
      const identity = await this.identity(did);
      if (!identity.aliases.includes(`at://${handle}`)) {
        throw new Error("Handle is not confirmed by its DID document.");
      }
      return `at://${did}/${uri.collection}/${uri.rkey}`;
    }
    return uri.toString();
  }

  async record(
    did: string,
    collection: string,
    rkey: string,
  ): Promise<unknown> {
    const { pds } = await this.identity(did);
    const url = new URL(`${pds}/xrpc/com.atproto.repo.getRecord`);
    url.search = new URLSearchParams({ repo: did, collection, rkey })
      .toString();
    const { value: result, errors } = validateRecord(
      await this.network.json(url, this.signal),
    );
    if (errors) throw new Error("Invalid getRecord response.");
    if (result.uri !== `at://${did}/${collection}/${rkey}`) {
      throw new Error("PDS returned a different record.");
    }
    return result.value;
  }

  post(uri: string): Promise<Post> {
    postUri(uri, true);
    let pending = this.#posts.get(uri);
    if (!pending) {
      pending = (async (): Promise<Post> => {
        try {
          const parsed = postUri(uri, true);
          const record = parsePost(
            await this.record(parsed.host, parsed.collection, parsed.rkey),
          );
          if (record.reply) {
            postUri(record.reply.root.uri, true);
            postUri(record.reply.parent.uri, true);
          }
          return { uri, record };
        } catch (error) {
          this.signal.throwIfAborted();
          return {
            uri,
            error: error instanceof Error ? error.message : "Post unavailable.",
          };
        }
      })();
      this.#posts.set(uri, pending);
    }
    return pending;
  }

  async author(did: string): Promise<Author> {
    const author: Author = { did };
    try {
      const identity = await this.identity(did);
      author.pds = identity.pds;
      const alias = identity.aliases.find((alias) =>
        /^at:\/\/[^/]+$/.test(alias)
      );
      author.handle = alias?.slice(5);
      const profile = await this.record(did, "app.bsky.actor.profile", "self")
        .then((value) => validateProfile(value).value).catch(() => undefined);
      author.displayName = profile?.displayName;
      if (profile?.avatar) {
        author.avatar = blobUrl(identity.pds, did, profile.avatar.ref.$link);
      }
    } catch { /* Author metadata must not hide a readable post. */ }
    this.signal.throwIfAborted();
    return author;
  }

  async batch(
    uris: string[],
    emit: (event: RecordEvent) => void,
  ): Promise<void> {
    const jobs: ({ type: "post" | "quote"; uri: string } | {
      type: "author";
      did: string;
    })[] = [...new Set(uris)].map((uri) => ({ type: "post", uri }));
    const authors = new Set<string>();
    const quotes = new Set<string>();
    let index = 0;
    await Promise.all(
      Array.from({ length: Math.min(8, jobs.length) }, async () => {
        while (index < jobs.length) {
          this.signal.throwIfAborted();
          const job = jobs[index++];
          if (job.type === "author") {
            emit({ type: "author", author: await this.author(job.did) });
            continue;
          }
          const post = await this.post(job.uri);
          emit({ type: job.type, post });
          const did = postUri(post.uri, true).host;
          if (!authors.has(did)) {
            authors.add(did);
            jobs.push({ type: "author", did });
          }
          const quote = job.type === "post" && post.record &&
            quoteUri(post.record);
          if (quote && !quotes.has(quote)) {
            try {
              postUri(quote, true);
              quotes.add(quote);
              jobs.push({ type: "quote", uri: quote });
            } catch { /* Invalid quotes must not hide the containing post. */ }
          }
        }
      }),
    );
  }

  async thread(
    input: string,
    emit: (event: ThreadEvent) => void,
  ): Promise<void> {
    const selected = await this.canonical(input);
    const submitted = await this.post(selected);
    if (!submitted.record) {
      throw new Error(`Cannot determine the thread root: ${submitted.error}`);
    }
    const root = submitted.record.reply?.root.uri ?? selected;
    emit({ type: "thread", selected, root });
    emit({ type: "post", post: submitted });
  }

  async backlinks(
    root: string,
    cursor: string | null,
  ): Promise<{ uris: string[]; cursor: string | null }> {
    const url = new URL(
      "https://backlinks.cerulea.blue/xrpc/blue.cerulea.backlinks.listBacklinks",
    );
    url.search = new URLSearchParams({
      target: root,
      limit: "100",
      ...(cursor === null ? {} : { cursor }),
    }).toString();
    const { value: page, errors } = validateBacklinks(
      await this.network.json(url, this.signal),
    );
    if (errors) throw new Error("Invalid backlinks response.");
    const uris = new Set<string>();
    for (const group of page.backlinks) {
      if (
        group.location !== "$.reply.root" && group.location !== "$.reply.parent"
      ) continue;
      for (const uri of group.uris) {
        try {
          postUri(uri, true);
          uris.add(uri);
        } catch {
          /* Backlinks can point to records outside the post collection. */
        }
      }
    }
    return { uris: [...uris], cursor: page.cursor };
  }
}
